import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosClient from "../../api/axiosClient";
import handleApiError from "../../utils/handleError";
import { ExamSession } from "../../redux/apiSlice/examinerSlice";
import {
  LabReadiness,
  mergeLabReadiness,
  needsLabPolling,
  storedLabReadiness,
} from "./labReadiness";

/* How often the sessions still waiting on a lab are re-checked. The provisioning
   cron on the backend runs every five minutes, but the readiness endpoint asks the
   lab service directly, so a lab that finished booting shows up here within the
   minute — which is the whole point: the examiner watches a status instead of
   probing with Verify clicks. */
const POLL_INTERVAL_MS = 30_000;

export type LabCheckOutcome = "ok" | "unavailable" | "error";

type LabReadinessState = {
  /* Live readiness per exam instance, keyed by id. Absent until first fetched — the
     caller merges it with what the list payload already implies. */
  readiness: Record<string, LabReadiness>;
  checking: Record<string, boolean>;
  /* True once the backend has told us it has no readiness endpoint. The column then
     runs on the list payload alone, which the table refreshes on its own timer. */
  liveUnavailable: boolean;
  /* Resolves to what happened, so a caller can fall back when this backend simply
     cannot answer — see the re-check button in ExaminerTable. */
  refresh: (
    id: string,
    options?: { silent?: boolean }
  ) => Promise<LabCheckOutcome>;
  /* Lets the verify call fold the readiness it got back with a 409 straight into the
     column, instead of the screen firing a second request to learn the same thing. */
  applyReadiness: (id: string, readiness: LabReadiness) => void;
};

/* A backend that predates the readiness endpoint answers with gin's own plain-text
   404 rather than the handler's {"status": ...} body. Telling the two apart matters:
   one means "this server cannot do live checks", the other means "that instance is
   gone", and only the first should switch live checking off for good. */
function isMissingEndpoint(error: any): boolean {
  if (error?.response?.status !== 404) return false;
  return typeof error.response.data?.status !== "string";
}

export default function useLabReadiness(
  sessions: ExamSession[] | undefined,
): LabReadinessState {
  const [readiness, setReadiness] = useState<Record<string, LabReadiness>>({});
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [liveUnavailable, setLiveUnavailable] = useState(false);

  const applyReadiness = useCallback((id: string, value: LabReadiness) => {
    setReadiness((current) => ({ ...current, [id]: value }));
  }, []);

  const refresh = useCallback(
    async (
      id: string,
      options?: { silent?: boolean }
    ): Promise<LabCheckOutcome> => {
      setChecking((current) => ({ ...current, [id]: true }));
      try {
        const response = await axiosClient.get(`examinstances/${id}/lab/status`);
        applyReadiness(id, response.data);
        return "ok";
      } catch (error: any) {
        /* A backend without the endpoint is not a failed request to report — there
           is nothing the examiner could do about it and the column has a working
           fallback. Record it, stay quiet, and let the caller take that route. */
        if (isMissingEndpoint(error)) {
          setLiveUnavailable(true);
          return "unavailable";
        }

        /* Everything else: polling stays quiet, since a notification every thirty
           seconds would be worse than the stale tag it warns about. A click on the
           re-check button is a question the examiner asked, so that one gets an
           answer. */
        if (!options?.silent) handleApiError(error);
        return "error";
      } finally {
        setChecking((current) => ({ ...current, [id]: false }));
      }
    },
    [applyReadiness]
  );

  const pending = useMemo(
    () =>
      liveUnavailable
        ? []
        : (sessions ?? [])
            .filter((session) =>
              needsLabPolling(
                session,
                mergeLabReadiness(
                  storedLabReadiness(session),
                  readiness[session.id],
                ),
              ),
            )
            .map((session) => session.id),
    [sessions, readiness, liveUnavailable],
  );

  /* The interval below reads the ids off a ref so that a row becoming ready — or the
     table reloading — does not tear down and restart the timer. */
  const pendingIds = useRef<string[]>(pending);

  useEffect(() => {
    pendingIds.current = pending;
  }, [pending]);

  /* Sequential, not Promise.all: each check costs the lab service an AWS lookup, and
     nothing here is urgent enough to fan out for. */
  const sweep = useCallback(
    async (only?: (id: string) => boolean) => {
      for (const id of pendingIds.current) {
        if (only && !only(id)) continue;
        await refresh(id, { silent: true });
      }
    },
    [refresh],
  );

  /* A row that has never been checked gets its live status straight away — waiting a
     poll interval to first learn a lab is ready is exactly the wait this replaces. */
  const everChecked = useRef<Set<string>>(new Set());
  const pendingKey = pending.join(",");

  useEffect(() => {
    sweep((id) => {
      if (everChecked.current.has(id)) return false;
      everChecked.current.add(id);
      return true;
    });
  }, [pendingKey, sweep]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      /* A backgrounded tab is nobody watching the column, and polling it would only
         spend AWS lookups. The next visible tick catches up. */
      if (document.hidden) return;
      sweep();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [sweep]);

  return { readiness, checking, liveUnavailable, refresh, applyReadiness };
}
